import ProductHeader from "./ProductHeader";
import React, { useEffect, useState } from "react";
import "./indexEMA21.css";

const points = value => value == null ? "—" : Number(value).toFixed(2);
const clock = value => value ? new Date(value).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—";
const words = value => (value || "Loading").replaceAll("_", " ");

const dayOf = value => value ? new Intl.DateTimeFormat("en-CA",{timeZone:"Asia/Kolkata",year:"numeric",month:"2-digit",day:"2-digit"}).format(new Date(value)) : "";
const bucket = value => value ? new Date(Math.floor(new Date(value).getTime()/300000)*300000).toISOString() : null;

export function signalRows(data) {
  const rows=new Map(), trades=data?.trades || [];
  for (const record of data?.indices?.NIFTY?.decision_history || []) {
    const d=record.decision || {};
    const trade=trades.find(t=>t.signal?.signal_time===d.signal_time);
    if (!d.signal_time || (data.session_date && dayOf(d.signal_time)!==data.session_date)) continue;
    rows.set(d.signal_time,{...record,trade});
  }
  for (const trade of trades) {
    const d=trade.signal;
    if (d?.signal_time && (!data.session_date || dayOf(d.signal_time)===data.session_date)) {
      rows.set(d.signal_time,{...(rows.get(d.signal_time) || {decision:d}),trade});
    }
  }
  return [...rows.values()].sort((a,b)=>b.decision.signal_time.localeCompare(a.decision.signal_time));
}

function decisionReason(status, decision) {
  const body = decision.body_points ?? (decision.signal ? Math.abs(decision.signal.close-decision.signal.open) : null);
  const reasons = {
    BODY_BELOW_10_POINTS: `No entry: candle body ${points(body)} points; minimum 10 required.`,
    GAP_BELOW_1_POINT: `No entry: close is ${points(Math.abs(decision.gap))} points beyond EMA21; minimum 1 required.`,
    WAITING_FOR_EMA_BREAK: "No entry: candle body did not cross EMA21.",
    SKIP_0915_CANDLE: "No entry: the 09:15 candle is excluded by the strategy.",
    WAITING_NEW_SIGNAL: "No entry: candle completed before the strategy was started or resumed.",
    EXIT_CANDLE_SKIPPED: "No entry: the previous trade's exit candle is excluded.",
    SAME_SIDE_SIGNAL_IGNORED: "No new setup: an earlier signal in this direction is still pending.",
    CANCELLED_OPPOSITE_SIGNAL: "No entry: replaced by a qualifying opposite signal.",
    TWO_CANDLE_WINDOW_EXPIRED: "No entry: the two-candle entry window expired.",
    WAITING_BREAKOUT_WINDOW: "Waiting for the next two candles to confirm entry.",
    WAITING_ENTRY_CANDLE_BREAK: "Waiting for NIFTY to break the signal close and move beyond the entry candle's open in the same direction.",
  };
  return reasons[status];
}

function Candle({details, time, eventTime, spot, signal=false}) {
  if (!time && !details?.time) return <>—</>;
  return <><strong>{clock(details?.time || bucket(time))}</strong>
    <div>O {points(details?.open)} · H {points(details?.high)} · L {points(details?.low)} · C {points(details?.close)}</div>
    {!signal && <small>{details?.source === "UNAVAILABLE" || !details ? "OHLC not recorded" : details.complete ? "Completed NIFTY candle" : `Forming NIFTY snapshot · as of ${clock(details.as_of)}`}</small>}
    {eventTime && <div>At {clock(eventTime)} · NIFTY {points(spot)}</div>}
  </>;
}

export default function IndexEMA21({ onClientAccounts }) {
  const [data, setData] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => {
    const controller = new AbortController(); let timer;
    const poll = async () => {
      try {
        const response = await fetch("/api/index-ema21", { signal: controller.signal, cache: "no-store" });
        const body = await response.json();
        if (!response.ok) throw Error(body.detail || "NIFTY EMA21 unavailable");
        if (!controller.signal.aborted) { setData(body); setError(""); }
      } catch (e) { if (!controller.signal.aborted) setError(e.message); }
      finally { if (!controller.signal.aborted) timer = setTimeout(poll, 2500); }
    };
    poll(); return () => { controller.abort(); clearTimeout(timer); };
  }, []);
  const action = async action => {
    setBusy(true);
    try {
      const response = await fetch(`/api/index-ema21/NIFTY/${action}`, { method: "POST" });
      const body = await response.json();
      if (!response.ok) throw Error(body.detail || "Request failed");
      setData(body); setError("");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const row = data?.indices?.NIFTY;
  return <section className="panel"><ProductHeader title="NIFTY EMA21 Breakout" mode="paper" paperOnly status={row ? `5-minute candles | ${row.running ? "SCANNING" : "ENTRIES PAUSED"}` : "Loading strategy status"} />
    <div className="panel-body">
      <p>5-minute candles · EMA21 · fifth ITM · one lot · Rs 1,00,000 paper capital per participating NIFTY client</p>
      <div className="strategy-command-header"><button disabled={busy || !row || Boolean(error) || Boolean(data?.error)} onClick={() => action(row?.running ? "pause" : "start")}>{busy ? "Updating…" : row?.running ? "Pause entries" : "Start strategy"}</button>
        <button onClick={onClientAccounts}>Manage participating clients</button></div>
      <p role="status">{words(row?.status)}</p>
      {(error || data?.error || row?.data_error) && <p role="alert">{error || data?.error || row?.data_error}</p>}
      <details><summary>Strategy rules</summary><p>Ignore 09:15. From 09:20, a candle body must cross EMA21 and close at least 1 NIFTY point beyond it, with an absolute open-to-close body of at least 10 points. Check the next 2 candles (candles 2 and 3 when the signal is candle 1) for live NIFTY to break the signal close in the same direction and move beyond the entry candle’s open. A qualifying opposite signal cancels and replaces the pending setup, restarting the 2-candle entry window. After an exit, ignore the exit candle and scan subsequent candles for a new signal. SL: signal open. Target: entry ± risk points (1:1), using actual NIFTY entry. No EMA or option-premium exits. Entries stop at 14:45; square-off at 15:12. Pausing entries leaves exits active.</p></details>
      <h3>EMA21 decisions · {data?.session_date || "Today"}</h3>
      <div className="positions-table-scroll"><table className="ema21-decisions"><thead><tr>
        <th>Signal candle (NIFTY / IST)</th><th>Side / Decision</th><th>EMA21 / Gap (min 1) / Body (min 10)</th>
        <th>Entry candle (NIFTY / IST)</th><th>Exit candle (NIFTY / IST)</th><th>NIFTY SL</th><th>NIFTY target</th><th>Exit trigger (NIFTY)</th><th>Option fills</th>
      </tr></thead><tbody>
        {signalRows(data).map(record=>{
          const d=record.decision, t=record.trade || {}, trigger=t.exit_trigger;
          const status=t.exit_time ? "CLOSED" : t.id ? t.exit_reason ? "EXIT_PENDING" : "OPEN" : record.status || d.status;
          const entryTime=t.entry_time || record.entry_time;
          return <tr key={d.signal_time}>
            <td><Candle details={d.signal} time={d.signal_time} signal/></td>
            <td>{d.side || "—"}<div>{words(status)}</div>{decisionReason(status,d) && <small className="ema21-decision-reason">{decisionReason(status,d)}</small>}</td>
            <td><div>EMA21 {points(d.ema21)}</div><div style={{color:d.gap>0?"#15803d":"#dc2626"}}>Gap {points(d.gap)}</div><div>Body {points(d.body_points ?? (d.signal ? Math.abs(d.signal.close-d.signal.open) : null))}</div></td>
            <td><Candle details={t.entry_candle_details} time={entryTime} eventTime={entryTime} spot={t.spot_entry ?? record.spot_entry}/></td>
            <td><Candle details={t.exit_candle_details} time={t.exit_time} eventTime={t.exit_time} spot={t.spot_exit}/></td>
            <td>{points(t.spot_stop ?? t.stop ?? record.stop ?? d.stop)}</td>
            <td>{points(t.spot_target ?? t.target ?? record.target)}</td>
            <td>{t.exit_reason ? <>{words(t.exit_reason)}<div>Trigger NIFTY {points(trigger?.spot)}</div><div>Level {points(trigger?.level)} · observed {clock(trigger?.time)}</div><small>{words(trigger?.source || "Trigger evidence not recorded")}{trigger?.candle_time && ` · candle ${clock(trigger.candle_time)}`}</small></> : "—"}{t.risk_error && <p role="alert">{t.risk_error}</p>}</td>
            <td>{t.contract || record.contract || "—"}<div>Entry premium {points(t.clients?.[0]?.entry ?? record.option_entry)}</div><div>Exit premium {points(t.clients?.[0]?.exit)}</div><small>Option fills/P&amp;L only; SL and target use NIFTY.</small></td>
          </tr>;
        })}
        {!signalRows(data).length && <tr><td colSpan={9}>No evaluated candles recorded today.</td></tr>}
      </tbody></table></div>
    </div></section>;
}
